<template>
  <div>
    <div class="meeting-info">
      <div class="font-size-small line-height-single">
        <strong>{{ formatTime(bookingStart) }} - {{ formatTime(bookingEnd) }}</strong>
      </div>
      <div class="font-size-medium meeting-title">{{ bookingTitle }}</div>
      <div class="font-size-medium text-color-secondary meeting-organizer" v-if="bookingOwnerName">
        {{ bookingOwnerName }}
      </div>
    </div>
    <template v-if="bookingViaPanel && !hasEnded">
      <div :class="{adhoc: !bookingOwnerName}" class="actions" v-if="checkedIn">
        <time-buttons :free-mins="freeMinutes" :loading="extendLoading" @time="extend" v-if="canExtend">
          Extend
        </time-buttons>
        <default-button class="extend" disabled v-else>Extend</default-button>
        <default-button :loading="bookingActionInProgress" @click="releaseBooking" class="check-out" color="red">
          Check Out
        </default-button>
      </div>
    </template>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import DefaultButton from '@/components/DefaultButton';
import TimeButtons from '@/components/TimeButtons';
import {bookingEnd, bookingStart} from '@/utils/bookings-v1';
import * as impl from '@/room-booking/v1/bookings';
import {minutes, toTime} from '@/utils/dates';

export default {
  name: 'HybridOfficeCurrentMeeting',
  components: {TimeButtons, DefaultButton},
  data() {
    return {
      bookingActionInProgress: false,
      extendLoading: false
    };
  },
  computed: {
    ...mapGetters('hybridOffice/bookings', ['now', 'currentBooking', 'nextBooking']),
    ...mapGetters('config', ['readOnlyMode']),
    canExtend() {
      return this.bookingViaPanel && this.freeMinutes >= 15;
    },
    bookingViaPanel() {
      const booking = this.currentBooking;
      if (!booking) return false;
      const bookingCreatedVia = booking && booking.createdVia && booking.createdVia.ref;
      const createableSnippet = this.$store.getters['createableSnippet'];
      const createdVia = createableSnippet && createableSnippet.createdVia && createableSnippet.createdVia.ref;
      return (bookingCreatedVia && bookingCreatedVia.path) === (createdVia && createdVia.path);
    },
    bookingStart() {
      const booking = this.currentBooking;
      if (!booking) return null;
      return bookingStart(booking, '');
    },
    bookingEnd() {
      const booking = this.currentBooking;
      if (!booking) return null;
      return bookingEnd(booking, '');
    },
    bookingTitle() {
      return this.currentBooking && this.currentBooking.title || 'Desk Booking';
    },
    bookingOwnerName() {
      return this.currentBooking && this.currentBooking.owner && this.currentBooking.owner.title;
    },
    requiresCheckIn() {
      const booking = this.currentBooking;
      if (!booking) return null;
      return impl.requiresCheckIn(booking, '');
    },
    checkedIn() {
      const booking = this.currentBooking;
      if (!booking) return false;
      return !this.requiresCheckIn || impl.hasCheckIn(booking, '');
    },
    hasEnded() {
      const t = this.bookingEnd;
      return t && t < this.now;
    },
    freeMinutes() {
      if (!this.nextBooking) return Infinity;
      const fromTime = impl.fromTime(this.nextBooking);
      const endTime = this.bookingEnd;
      return Math.trunc((toTime(fromTime) - endTime.getTime()) / minutes);
    }
  },
  methods: {
    ...mapActions('hybridOffice/bookings', ['extendBooking', 'release']),
    extend(mins) {
      this.extendLoading = true;
      this.extendBooking({booking: this.currentBooking, mins})
          .catch(err => {
            this.$logger.error(err);
            this.$notify.showError('Error extending meeting, please try again');
          })
          .finally(() => {
            this.extendLoading = false;
          });
    },
    async releaseBooking() {
      const booking = this.currentBooking;
      if (!booking || !booking.ref) {
        throw new Error(`no booking ref`);
      }
      this.bookingActionInProgress = true;
      try {
        await this.release({booking});
      } catch (e) {
        this.$notify.showError(`Error 'releasing' booking`);
        this.$logger.error(`error 'releasing' booking`, e);
      } finally {
        this.bookingActionInProgress = false;
      }
    }
  }
};
</script>

<style scoped>
.meeting-info {
  max-width: calc(10 * var(--grid-size));
}

.meeting-title {
  line-height: 1.4em;
  max-height: 2.8em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.meeting-organizer {
  line-height: 1.4em;
}

.actions,
.check-in {
  margin-top: 10px;
  /*--vertical-grid-offset: 2;
  position: absolute;
  top: calc(var(--vertical-grid-offset) * var(--grid-size));*/
}

.actions {
  width: calc(2 * var(--button-width) + 16px);
}

.actions.adhoc {
  top: calc((var(--vertical-grid-offset) * var(--grid-size)) - var(--button-height));
}

.check-out {
  margin-left: 16px;
}

.reservation-time {
  opacity: 0.5;
  font-weight: normal;
}
</style>
